import { useState, type FormEvent } from 'react';
import { Download, KeyRound, Lock, Trash2, Wrench } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { formatTime, hasPermission } from '@ste/shared';
import { rootFetch, useAuth } from '../../api/auth.ts';
import { useConnection, useCurrentSite } from '../../api/connection.ts';
import { useBaseRole, useRole } from '../../api/role.ts';
import { ChangeLogCard } from './ChangeLogCard.tsx';
import { useElevation, useServiceAccessCommands } from '../../api/serviceAccess.ts';
import { Banner, Button, Card, PageHeader, SegmentedControl } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { useTheme, type Theme } from '../../theme/theme.ts';
import { CommandError } from '../charging/CommandError.tsx';

const inputClass = 'mt-1 block min-h-11 w-full rounded-lg border border-border bg-surface-2 px-3 text-text';

/** settings for everyone; the owner opens the installer area here with the service password */
export function SettingsPage() {
  const user = useAuth((s) => s.user);
  const mode = useConnection((s) => s.mode);
  const site = useCurrentSite();
  const baseRole = useBaseRole();
  const role = useRole();
  const { theme, setTheme } = useTheme();

  return (
    <div className="space-y-4">
      <PageHeader title={t('settings.title')} />

      <Card title={t('settings.account')}>
        <dl className="grid gap-2 text-sm sm:grid-cols-[12rem_1fr]">
          <dt className="text-muted">{t('team.name')}</dt>
          <dd>{user?.name}</dd>
          <dt className="text-muted">{t('auth.email')}</dt>
          <dd className="break-all">{user?.email}</dd>
          {baseRole && (
            <>
              <dt className="text-muted">{t('settings.roleOnSite')}</dt>
              <dd>
                {baseRole === 'admin' ? t('team.role.admin') : t(`sites.role.${baseRole}`)}
                {site && ` · ${site.title}`}
              </dd>
            </>
          )}
        </dl>
      </Card>

      {baseRole === 'owner' && <ServiceAccessCard />}

      {/* who changed what (ADR-0024): owners, installers and admins of this site */}
      {role && hasPermission(role, 'audit:read') && (mode === 'gateway' || site) && <ChangeLogCard />}

      <Card title={t('settings.appearance')}>
        <SegmentedControl<Theme>
          label={t('common.theme')}
          options={[
            { value: 'system', label: t('common.themeSystem') },
            { value: 'light', label: t('common.themeLight') },
            { value: 'dark', label: t('common.themeDark') },
          ]}
          value={theme}
          onChange={setTheme}
        />
      </Card>

      {mode === 'cloud' ? (
        <>
          <PasswordCard />
          <DataCard />
        </>
      ) : (
        <Card title={t('settings.password')}>
          <p className="text-sm text-muted">{t('settings.passwordLocal')}</p>
        </Card>
      )}
    </div>
  );
}

function ServiceAccessCard() {
  const elevation = useElevation();
  const commands = useServiceAccessCommands();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    commands.unlock.mutate(password, {
      onSuccess: () => {
        setPassword('');
        navigate('/installateur');
      },
    });
  };

  return (
    <Card title={t('settings.service')}>
      {elevation ? (
        <div className="space-y-3">
          <Banner
            tone="accent"
            title={t('settings.serviceOpen', { time: formatTime(new Date(elevation.until)) })}
          />
          <div className="flex flex-wrap gap-2">
            <Link
              to="/installateur"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 font-medium text-on-accent hover:brightness-110"
            >
              <Wrench size={18} /> {t('settings.serviceGo')}
            </Link>
            <Button variant="secondary" onClick={commands.lock}>
              <Lock size={18} /> {t('settings.serviceLock')}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted">{t('settings.serviceIntro')}</p>
          <label className="block max-w-sm">
            <span className="text-sm text-muted">{t('settings.servicePassword')}</span>
            <input
              className={`${inputClass} font-mono tracking-wider uppercase`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX"
            />
          </label>
          {commands.unlock.isError && <CommandError error={commands.unlock.error} />}
          <Button type="submit" disabled={!password.trim() || commands.unlock.isPending}>
            <KeyRound size={18} /> {t('settings.serviceUnlock')}
          </Button>
        </form>
      )}
    </Card>
  );
}

function PasswordCard() {
  const logout = useAuth((s) => s.logout);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<unknown>();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await rootFetch<void>('/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) });
      // all sessions end with a new password, this one included
      setDone(true);
      setTimeout(() => void logout(), 2500);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('settings.password')}>
      {done ? (
        <Banner tone="accent" title={t('settings.passwordDone')} />
      ) : (
        <form onSubmit={submit} className="grid max-w-xl gap-3 sm:grid-cols-2">
          <label>
            <span className="text-sm text-muted">{t('settings.passwordCurrent')}</span>
            <input
              type="password"
              className={inputClass}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label>
            <span className="text-sm text-muted">{t('settings.passwordNext')}</span>
            <input
              type="password"
              className={inputClass}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          {error !== undefined && (
            <div className="sm:col-span-2">
              <CommandError error={error} />
            </div>
          )}
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy || !current || next.length < 10}>
              {t('settings.passwordSave')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function DataCard() {
  const logout = useAuth((s) => s.logout);
  const [deleting, setDeleting] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>();

  async function download() {
    setError(undefined);
    try {
      const data = await rootFetch<unknown>('/me/export');
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ste-connect-daten.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    }
  }

  async function remove(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    try {
      await rootFetch<void>('/me', { method: 'DELETE', body: JSON.stringify({ password }) });
      await logout();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <Card title={t('settings.data')}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void download()}>
            <Download size={18} /> {t('settings.export')}
          </Button>
          {!deleting && (
            <Button variant="ghost" onClick={() => setDeleting(true)}>
              <Trash2 size={18} /> {t('settings.delete')}
            </Button>
          )}
        </div>
        {deleting && (
          <form onSubmit={remove} className="max-w-sm space-y-3 border-t border-border pt-4">
            <p className="text-sm text-muted">{t('settings.deleteText')}</p>
            <label className="block">
              <span className="text-sm text-muted">{t('settings.deleteConfirm')}</span>
              <input
                type="password"
                className={inputClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!password}>
                {t('settings.deleteFinal')}
              </Button>
              <Button variant="ghost" onClick={() => (setDeleting(false), setPassword(''))}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
        {error !== undefined && <CommandError error={error} />}
      </div>
    </Card>
  );
}
